
import { defineComponent, onMounted, watch, computed, ref } from "vue";
import VueApexCharts from "vue3-apexcharts";
import { useStore } from "vuex";
import { Actions } from "@/store/enums/StoreEnums";
import ChartsConfigurations from "@/core/config/charts/ChartsConfigurations";
import { Names } from "@/core/config/enums/SitePaths";
import { useRoute, useRouter } from "vue-router";
import ApiService from "@/core/services/ApiService";
import { Endpoints } from "@/api/ApiEndpoints";
export default defineComponent({
  name: "AdminDashboard",
  components: {
    apexchart: VueApexCharts,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    let key = "test";

    const chartOptions = {
      labels: [],
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 200,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
    };
    const dashboardSummary = ref({
      users: 0,
      user_Period: 0,
      adReward: 0,
      adReward_Period: 0,
      missions_Completed: 0,
      missions_Completed_Period: 0,
      coupons: 0,
      coupons_Period: 0,
      coins_Spent: 0,
      coins_Spent_Period: 0,
    });

    const formatDate = (d: Date) => d.toISOString().slice(0, 10);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    const startDateIso = ref<string>((route.query.startDate as string) || formatDate(yesterday));
    const endDateIso = ref<string>((route.query.endDate as string) || formatDate(today));

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const coinsPerMonth = ref<Array<{ month: number; year: number; coins: number }>>([]);
    const usersPerMonth = ref<Array<{ month: number; year: number; users: number }>>([]);
    const usersPerCountryPerMonth = ref<
      Array<{
        month: number;
        year: number;
        users: number;
        countryId: number;
        countryName: string;
      }>
    >([]);
    const showRangePicker = ref(false);

    const loadDashboardSummary = async () => {
      try {
        const params: Record<string, string> = {};
        if (startDateIso.value) params.startDate = startDateIso.value;
        if (endDateIso.value) params.endDate = endDateIso.value;

        const response = await ApiService.get(Endpoints.ADMIN_DASHBOARD, "", params);
        const payload = response?.data ?? {};
        dashboardSummary.value = {
          users: payload.users ?? 0,
          user_Period: payload.user_Period ?? 0,
          adReward: payload.adReward ?? 0,
          adReward_Period: payload.adReward_Period ?? 0,
          missions_Completed: payload.missions_Completed ?? 0,
          missions_Completed_Period: payload.missions_Completed_Period ?? 0,
          coupons: payload.coupons ?? 0,
          coupons_Period: payload.coupons_Period ?? 0,
          coins_Spent: payload.coins_Spent ?? 0,
          coins_Spent_Period: payload.coins_Spent_Period ?? 0,
        };
        coinsPerMonth.value = Array.isArray(payload.coinsPerMonth) ? payload.coinsPerMonth : [];
        usersPerMonth.value = Array.isArray(payload.usersPerMonth) ? payload.usersPerMonth : [];
        usersPerCountryPerMonth.value = Array.isArray(payload.userPerCountryPerMonth)
          ? payload.userPerCountryPerMonth
          : [];
      } catch (error) {
        console.error("Failed to load dashboard summary", error);
      }
    };

    onMounted(() => {
      loadDashboardSummary();
    });

    const getClients = computed(() => {
      return store.getters.getClientSales;
    });
    const getLastMonthsSales = computed(() => {
      return store.getters.getLastMonthsSales;
    });

    const getProductSales = computed(() => {
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties

      return store.getters.getProductSales({
        fillCollor: "#F50242",
        name: "Products Top 5",
      });
    });
    const getProductLabels = computed(() => {
      return store.getters.getProductLabels;
    });
    const series = [44, 55, 13, 43, 22];

    const euro = (val: number | null | undefined) =>
      new Intl.NumberFormat("el-GR", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(val ?? 0));
    const numberNoCurrency = (val: number | null | undefined) =>
      new Intl.NumberFormat("el-GR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(val ?? 0));

    const displayRange = computed(() => {
      const fmt = (iso: string) => {
        if (!iso) return "";
        const [y, m, d] = iso.split("-");
        if (!y || !m || !d) return "";
        return `${d}-${m}-${y}`;
      };
      const start = fmt(startDateIso.value);
      const end = fmt(endDateIso.value);
      return start && end ? `${start} | ${end}` : start || end || "";
    });

    const adRewardPeriod = computed(() => dashboardSummary.value.adReward_Period);
    const adRewardTotal = computed(() => dashboardSummary.value.adReward);
    const missionsCompletedPeriod = computed(() => dashboardSummary.value.missions_Completed_Period);
    const missionsCompletedTotal = computed(() => dashboardSummary.value.missions_Completed);
    const formattedCoinsPeriod = computed(() => numberNoCurrency(dashboardSummary.value.coins_Spent_Period));
    const formattedCoinsTotal = computed(() => numberNoCurrency(dashboardSummary.value.coins_Spent));
    const coinsChartSeries = computed(() => [
      {
        name: "Coins Spent",
        data: (coinsPerMonth.value || []).map((p) => ({
          x: `${monthNames[(p.month ?? 1) - 1] ?? p.month} ${p.year}`,
          y: p.coins ?? 0,
        })),
      },
    ]);
    const coinsChartOptions = computed(() => ({
      chart: { toolbar: { show: false }, height: 380 },
      xaxis: {
        type: "category",
        labels: { style: { colors: "#fff" } },
      },
      yaxis: {
        labels: {
          style: { colors: "#fff" },
          formatter: (val: number) => numberNoCurrency(val),
        },
      },
      stroke: { curve: "smooth", width: 3 },
      dataLabels: { enabled: false },
      tooltip: {
        theme: "dark",
        y: { formatter: (val: number) => numberNoCurrency(val) },
      },
      colors: ["#F50242"],
    }));

    const usersChartSeries = computed(() => [
      {
        name: "Users",
        data: (usersPerMonth.value || []).map((p) => ({
          x: `${monthNames[(p.month ?? 1) - 1] ?? p.month} ${p.year}`,
          y: p.users ?? 0,
        })),
      },
    ]);

    const usersChartOptions = computed(() => ({
      chart: { toolbar: { show: false }, height: 380 },
      xaxis: {
        type: "category",
        labels: { style: { colors: "#fff" } },
      },
      yaxis: {
        labels: { style: { colors: "#fff" } },
      },
      tooltip: { theme: "dark" },
      plotOptions: {
        bar: {
          columnWidth: "45%",
          borderRadius: 6,
        },
      },
      dataLabels: { enabled: false },
      colors: ["#F50242"],
    }));

    const usersCountryChartData = computed(() => {
      const topCountriesLimit = 8;
      const items = Array.isArray(usersPerCountryPerMonth.value)
        ? usersPerCountryPerMonth.value
        : [];
      const months = new Map<string, { month: number; year: number; label: string }>();
      const valuesByMonth = new Map<string, Map<string, number>>();
      const countryNames = new Map<string, string>();

      items.forEach((p) => {
        const month = Number(p.month ?? 0);
        const year = Number(p.year ?? 0);
        if (!month || !year) return;
        const monthKey = `${year}-${String(month).padStart(2, "0")}`;
        const label = `${monthNames[month - 1] ?? month} ${year}`;
        months.set(monthKey, { month, year, label });

        const countryName = p.countryName || `Country ${p.countryId ?? ""}`.trim();
        const countryKey = `${p.countryId ?? ""}|${countryName}`;
        countryNames.set(countryKey, countryName);

        const monthMap = valuesByMonth.get(monthKey) ?? new Map<string, number>();
        const prev = monthMap.get(countryKey) ?? 0;
        monthMap.set(countryKey, prev + Number(p.users ?? 0));
        valuesByMonth.set(monthKey, monthMap);
      });

      const sortedMonths = Array.from(months.values()).sort((a, b) =>
        a.year === b.year ? a.month - b.month : a.year - b.year
      );
      const monthKeys = sortedMonths.map((m) => `${m.year}-${String(m.month).padStart(2, "0")}`);
      const monthLabels = sortedMonths.map((m) => ({ key: `${m.year}-${String(m.month).padStart(2, "0")}`, label: m.label }));

      return {
        monthKeys,
        monthLabels,
        valuesByMonth,
        countryNames,
      };
    });

    const usersCountryChartMonths = computed(() => usersCountryChartData.value.monthLabels);
    const selectedUsersCountryMonth = ref<string>("");

    watch(
      usersCountryChartMonths,
      (months) => {
        if (!months || months.length === 0) {
          selectedUsersCountryMonth.value = "";
          return;
        }
        if (months.length === 1) {
          selectedUsersCountryMonth.value = months[0].key;
          return;
        }
        if (!selectedUsersCountryMonth.value || !months.find((m) => m.key === selectedUsersCountryMonth.value)) {
          selectedUsersCountryMonth.value = months[months.length - 1].key;
        }
      },
      { immediate: true }
    );

    const usersCountryChartSeries = computed(() => {
      const { valuesByMonth, countryNames } = usersCountryChartData.value;
      if (!selectedUsersCountryMonth.value) return { series: [], labels: [] };
      const monthMap = valuesByMonth.get(selectedUsersCountryMonth.value) ?? new Map<string, number>();

      const totals = Array.from(monthMap.entries()).map(([countryKey, value]) => ({
        countryKey,
        value,
      }));
      totals.sort((a, b) => b.value - a.value);

      const top = totals.slice(0, 8);
      const rest = totals.slice(8);
      const series = top.map((t) => t.value);
      const labels = top.map((t) => countryNames.get(t.countryKey) ?? "Country");

      if (rest.length > 0) {
        const othersTotal = rest.reduce((sum, t) => sum + t.value, 0);
        series.push(othersTotal);
        labels.push("Others");
      }

      return { series, labels };
    });
    const usersCountryPieSeries = computed(() => usersCountryChartSeries.value.series);

    const usersCountryChartOptions = computed(() => ({
      chart: { toolbar: { show: false }, height: 320 },
      labels: usersCountryChartSeries.value.labels,
      legend: { labels: { colors: "#fff" }, position: "bottom", floating: true, offsetY: 8 },
      grid: { padding: { bottom: 30 } },
      tooltip: {
        theme: "dark",
        y: { formatter: (val: number) => Math.round(val).toString() },
        custom: ({ series, seriesIndex, w }) => {
          const label = w.globals.labels[seriesIndex] ?? "";
          const value = series[seriesIndex] ?? 0;
          return `<div class="apexcharts-tooltip-title">${label}</div><div class="apexcharts-tooltip-series">${Math.round(value)}</div>`;
        },
      },
      plotOptions: {
        pie: {
          customScale: 1,
          dataLabels: { offset: 0 },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (_val: number, opts: any) => {
          const label = opts?.w?.globals?.labels?.[opts.seriesIndex] ?? "";
          const value = opts?.w?.globals?.series?.[opts.seriesIndex] ?? 0;
          if (value < 1) return "";
          return `${label}: ${Math.round(value)}`;
        },
        style: { colors: ["#fff"] },
        background: {
          enabled: true,
          foreColor: "#fff",
          color: "#010C3D",
          padding: 4,
          borderRadius: 4,
          opacity: 1,
          borderWidth: 0,
        },
      },
      colors: usersCountryChartSeries.value.labels.map((_, index) => {
        const hue = Math.round((index * 360) / Math.max(usersCountryChartSeries.value.labels.length, 1));
        return `hsl(${hue}, 75%, 55%)`;
      }),
    }));

    const syncQueryToInputs = () => {
      if (typeof route.query.startDate === "string") {
        startDateIso.value = route.query.startDate;
      }
      if (typeof route.query.endDate === "string") {
        endDateIso.value = route.query.endDate;
      }
    };

    const applyDateFilter = () => {
      router.replace({
        query: {
          ...route.query,
          startDate: startDateIso.value || undefined,
          endDate: endDateIso.value || undefined,
        },
      });
      loadDashboardSummary();
    };

    const clearDateFilter = () => {
      startDateIso.value = "";
      endDateIso.value = "";
      const { startDate: _s, endDate: _e, ...rest } = route.query as Record<string, any>;
      router.replace({ query: { ...rest } });
      loadDashboardSummary();
    };

    const setToday = () => {
      const now = new Date();
      startDateIso.value = formatDate(now);
      endDateIso.value = formatDate(now);
    };

    const setLastDay = () => {
      const y = new Date();
      y.setDate(y.getDate() - 1);
      startDateIso.value = formatDate(y);
      endDateIso.value = formatDate(new Date());
    };

    const setLastMonth = () => {
      const start = new Date();
      start.setMonth(start.getMonth() - 1);
      start.setDate(1);
      const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
      startDateIso.value = formatDate(start);
      endDateIso.value = formatDate(end);
    };

     const setThisMonth = () => {
      const start = new Date();
      start.setMonth(start.getMonth());
      start.setDate(1);
      const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
      startDateIso.value = formatDate(start);
      endDateIso.value = formatDate(end);
    };

    const setThisYear = () => {
      const year = today.getFullYear();
      startDateIso.value = formatDate(new Date(year, 0, 1));
      endDateIso.value = formatDate(new Date(year, 11, 31));
    };

    watch(
      () => route.query,
      () => syncQueryToInputs()
    );

    return {
      ChartsConfigurations,

      series,
      chartOptions,
      getProductSales,
      getProductLabels,
      getClients,

      getLastMonthsSales,
      dashboardSummary,
      adRewardPeriod,
      adRewardTotal,
      formattedCoinsPeriod,
      formattedCoinsTotal,
      coinsChartSeries,
      coinsChartOptions,
      usersChartSeries,
      usersChartOptions,
      usersCountryChartSeries: usersCountryPieSeries,
      usersCountryChartOptions,
      usersCountryChartMonths,
      selectedUsersCountryMonth,
      showRangePicker,
      startDateIso,
      endDateIso,
      displayRange,
      setToday,
      setLastDay,
      setLastMonth,
      setThisMonth,
      setThisYear,
      applyDateFilter,
      clearDateFilter,
    };
  },
});
